export const LEGACY_IMAGE_VERSION = 'legacy';

/**
 * The CON-70 variant widths a donor reader may ask for, ascending.
 *
 * CON-70 writes 480 / 960 / 1440 / 1920 and never upscales, so a variant wider
 * than the original is simply absent. No record carries the original's width,
 * so a reader cannot know whether 1920 exists; 1440 is the widest a hero can
 * render (the hosted rail caps at 960 CSS px, 2× on a retina display) and is
 * the widest step this list names. Adding 1920 back needs an `imageWidth` on
 * the record first (CON-72 record, deviation A).
 */
export const VARIANT_WIDTHS = [480, 960, 1440];

/**
 * The non-WebP variant format, as images-backend spells it in the key.
 *
 * images-backend's variant writer normalises `jpg` to `jpeg`, so it writes
 * `{name}.w{width}.webp` and `{name}.w{width}.jpeg` — never `.jpg`. A reader
 * that asks for `.w960.jpg` gets a 404 (CON-72). Originals are a different
 * rule: they keep their `.jpg` key.
 */
export const VARIANT_FALLBACK_FORMAT = 'jpeg';

const present = (value) => value !== undefined && value !== null && value !== '';

/**
 * Resolve a persisted image version without introducing a render-time buster.
 * Backfills seed the owning version field from S3 LastModified for old records;
 * records that predate that field share one stable legacy cache key.
 */
export function imageVersionFor(
  record,
  { versionField = 'imageVersion' } = {},
) {
  if (present(record?.[versionField])) return record[versionField];
  return LEGACY_IMAGE_VERSION;
}

/** `{base}/{type}` with duplicate slashes and a doubled type prefix removed. */
function imageBasePath({ base, type }) {
  const normalizedBase = String(base).replace(/\/+$/, '');
  const normalizedType = String(type || '').replace(/^\/+|\/+$/g, '');
  return normalizedType && normalizedBase.endsWith(`/${normalizedType}`)
    ? normalizedBase
    : `${normalizedBase}${normalizedType ? `/${normalizedType}` : ''}`;
}

const versionQuery = (version) =>
  present(version) ? `?v=${encodeURIComponent(String(version))}` : '';

const stripDot = (value, fallback) => String(value || fallback).replace(/^\./, '');

/** Build the canonical URL for an image-bucket object. */
export function imageUrl({ base, type, name, ext = 'png', version }) {
  if (!base || !name) return '';

  return `${imageBasePath({ base, type })}/${encodeURIComponent(String(name))}.${stripDot(
    ext,
    'png',
  )}${versionQuery(version)}`;
}

/**
 * Build the URL of one CON-70 variant: `{type}/{name}.w{width}.{format}`, with
 * the same version query as the original so the two bust together.
 */
export function imageVariantUrl({ base, type, name, width, format = 'webp', version }) {
  if (!base || !name || !width) return '';

  return `${imageBasePath({ base, type })}/${encodeURIComponent(String(name))}.w${width}.${stripDot(
    format,
    'webp',
  )}${versionQuery(version)}`;
}

/**
 * The `srcset` string over the variant widths of one source, e.g.
 * `"…/x.w480.webp?v=1 480w, …/x.w960.webp?v=1 960w, …/x.w1440.webp?v=1 1440w"`.
 */
export function imageSrcSet({
  base,
  type,
  name,
  version,
  widths = VARIANT_WIDTHS,
  format = 'webp',
}) {
  if (!base || !name) return '';

  return widths
    .map((width) => `${imageVariantUrl({ base, type, name, width, format, version })} ${width}w`)
    .join(', ');
}

/**
 * Whether a record is one CON-70 may have written variants for.
 *
 * There is no record field that proves variants exist — the images Lambda
 * writes no table, and the CON-69 backfill stamps `imageVersion` from S3
 * LastModified on originals that may have none. A real version is the only
 * signal available, so a reader that emits `srcset` on it must also degrade to
 * the plain original when a variant 404s (HeaderImage does).
 */
export function hasImageVariants(record, opts) {
  return imageVersionFor(record, opts) !== LEGACY_IMAGE_VERSION;
}
